// [uTheory](http://utheory.com) - Copyright (c) Gregory Ristow 2014
//
// ## Sort accidentals by +/- half-step changes
//
// Include description of object & useful info here, please!
import type {
  InteractiveOptions,
  LayoutSettings,
  LayoutItem,
  UserData,
} from './interactivePrototype';
import InteractivePrototype from './interactivePrototype';
import CheckMark from './checkMark';
import ListSorter from './listSorter';
import * as Font from '../uTheory.Font/font';

interface InversionSorterOptions extends InteractiveOptions {
  chordType: 'triads' | 'sevenths';
}

interface InversionSorterLayout extends LayoutSettings {
  sortDiv?: LayoutItem;
}

class InversionSorter extends InteractivePrototype {
  attempts: number;

  listDiv: HTMLDivElement;

  declare options: InversionSorterOptions;

  listSorter: ListSorter;

  declare layout: InversionSorterLayout;

  constructor(displayDiv: HTMLDivElement, options: InversionSorterOptions, userData?: UserData) {
    super(displayDiv, options);
    this.settings = {
      instructions: true,
      letters: { checkButton: true, letterButtons: false },
      progressTracker: true,
      checkMark: {},
    };

    this.layout = {
      letters: { percent: 10 },
    };

    this.init(displayDiv, options, userData);
  }

  init(displayDiv: HTMLDivElement, options: InversionSorterOptions, userData?: UserData) {
    // now call the prototype's init function:
    return super.init(displayDiv, options, userData).then(() => {
      this.progressTracker.start();
      this.letterPanel.addCheckCallback(this.checkAnswer, this);
      this.attempts = 0;
    });
  }

  displayInstructions() {
    this.instructionsDiv.innerHTML =
      'Re-arrange the figures and bass notes to match the inversion in the left hand column.';
  }

  createCustomDivs() {
    this.createDiv('listDiv', { width: '100%', height: 'auto' });
    this.displayDiv.removeChild(this.listDiv);
    this.displayDiv.insertBefore(this.listDiv, this.lettersDiv);
    this.checkMark = new CheckMark(this.listDiv);
  }

  static getLists(isSeventh: boolean) {
    const inversions = [
      'Inversions:',
      'Root Position',
      '1st Inversion',
      '2nd Inversion',
      '3rd Inversion',
    ];
    const bassNotes = ['Bass Note:', 'Root', 'Third', 'Fifth', 'Seventh'];
    const longFigures = ['Figures (Long):', '53', '63', '64'].map(font);
    const shortFigures = ['Figures (Short):', ' ', '6', '64'].map(font);
    const longFigures7 = ['Figures (Long):', '753', '653', '643', '642'].map(font);
    const shortFigures7 = ['Figures (Short):', '7', '65', '43', '42'].map(font);

    function font(str, index) {
      if (index === 0 || str === ' ') return str;
      return Font.toHTML('figuredBass', str);
    }

    return [inversions, bassNotes]
      .concat(isSeventh ? [longFigures7, shortFigures7] : [longFigures, shortFigures])
      .map((arr) => arr.slice(0, isSeventh ? 5 : 4));
  }

  initializeCustomElements() {
    const lists = InversionSorter.getLists(this.options.chordType === 'sevenths');
    this.listSorter = new ListSorter(this.listDiv, lists);
    this.layout.sortDiv = {
      div: this.listDiv,
      visible: true,
      percent: 85,
      callback: this.listSorter.resize.bind(this.listSorter),
    };
  }

  gameOver() {
    this.lettersDiv.style.display = 'none';
    this.listSorter.gameOver();
    super.gameOver();
  }

  exit() {
    this.listSorter?.gameOver();
    super.exit();
  }

  checkAnswer() {
    const answers = this.listSorter.check();

    this.attempts++;
    const gameOver = answers.wrong === 0;
    this.progressTracker.multipleAnswers(
      [
        {
          skill:
            this.options.chordType === 'sevenths'
              ? 'seventh_chord_figured_bass'
              : 'triad_figured_bass',
          right: answers.right,
          wrong: answers.wrong,
          questions: answers.right + answers.wrong,
        },
      ],
      answers.right + answers.wrong,
      gameOver
    );
    if (gameOver) {
      this.checkMark.check(2);
      this.gameOver();
    } else {
      this.checkMark.x();
      if (this.attempts > 1) this.listSorter.hint();
    }
  }
}

export default InversionSorter;
